import { CheckCircle, ChevronDown, ChevronUp, XCircle } from "lucide-react";
import type React from "react";
import { useState } from "react";
import type { SyncResult } from "../services/api/syncApi";
import { Button } from "./ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";

interface SyncResultsModalProps {
	isOpen: boolean;
	onClose: () => void;
	result: SyncResult;
}

const SyncResultsModal: React.FC<SyncResultsModalProps> = ({
	isOpen,
	onClose,
	result,
}) => {
	const [showErrors, setShowErrors] = useState(false);

	const durationMs =
		new Date(result.completed_at).getTime() -
		new Date(result.started_at).getTime();
	const durationSec = (durationMs / 1000).toFixed(1);
	const hasErrors = result.errors > 0;

	const stats = [
		{
			label: "Created",
			value: result.created,
			colorClass: "text-(--color-success)",
		},
		{
			label: "Updated",
			value: result.updated,
			colorClass: "text-(--color-primary)",
		},
		{
			label: "Stale",
			value: result.stale,
			colorClass: "text-(--color-warning)",
		},
		{
			label: "Errors",
			value: result.errors,
			colorClass:
				result.errors > 0
					? "text-(--color-error-500)"
					: "text-(--color-muted-foreground)",
		},
	] as const;

	return (
		<Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="max-w-[500px]">
				<DialogHeader>
					<div className="flex items-center justify-center">
						{hasErrors ? (
							<XCircle size={40} className="text-(--color-error-500)" />
						) : (
							<CheckCircle size={40} className="text-(--color-success)" />
						)}
					</div>
					<DialogTitle className="text-center">Sync Complete</DialogTitle>
				</DialogHeader>
				<DialogDescription className="text-center">
					Completed in {durationSec}s
				</DialogDescription>

				<div className="grid grid-cols-4 gap-3">
					{stats.map(({ label, value, colorClass }) => (
						<div
							key={label}
							className="rounded-(--radius-sm) border border-(--color-border) bg-(--color-muted) p-3 text-center"
						>
							<div className={`font-bold text-2xl ${colorClass}`}>{value}</div>
							<div className="text-(--color-muted-foreground) text-xs">
								{label}
							</div>
						</div>
					))}
				</div>

				{hasErrors && (
					<div className="overflow-hidden rounded-(--radius-sm) border border-(--color-error-200)">
						<button
							type="button"
							className="flex w-full items-center justify-between bg-(--color-error-50) px-3.5 py-2.5 font-semibold text-(--color-error-700) text-sm"
							onClick={() => setShowErrors((v) => !v)}
						>
							<span>
								{result.errors} error{result.errors > 1 ? "s" : ""} — click to{" "}
								{showErrors ? "hide" : "show"} details
							</span>
							{showErrors ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
						</button>

						{showErrors && (
							<div className="max-h-56 overflow-auto bg-white">
								{result.errors_detail.map((err, i) => (
									<div
										// biome-ignore lint/suspicious/noArrayIndexKey: error details have no stable key
										key={i}
										className="border-(--color-error-100) border-t px-3.5 py-2.5 text-[0.813rem]"
									>
										<div className="font-semibold text-(--color-foreground)">
											{err.email ?? err.cognito_sub}
											<span className="ml-2 font-normal text-(--color-muted-foreground) text-xs">
												({err.operation})
											</span>
										</div>
										<div className="mt-0.5 text-(--color-error-600)">
											{err.error}
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				)}

				<DialogFooter>
					<Button onClick={onClose}>Done</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default SyncResultsModal;
